import { Collapse, FilterChipGroup, Grid, InputLabel, Paper, makeStyles } from '@paintscout/ui'
import { useClientOptions, useUser } from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import type { InFilter, RangeFilter, ValueFilter } from 'json-to-lucene'
import React from 'react'
import classnames from 'classnames'
import {
  formatCurrency,
  formatName,
  getObjectLabels,
  getQuoteTags,
  getQuoteTypeOptions,
  getStatusOptions
} from '@paintscout/util/builder'
import { useSearchUsersQuery } from '@paintscout/api'
import { removeAuth0Prefix } from '@paintscout/util/users'
import moment from 'moment'
import { useFlags } from 'shared/components/common/FlagsProvider'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },
  paper: {
    padding: theme.spacing(4, 6),
    marginBottom: theme.spacing(1)
  },
  chipGroup: {
    margin: theme.spacing(1, 0),
    gridTemplateColumns: `repeat(auto-fill, minmax(120px, 1fr))`
  },
  longChip: {
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: `1fr`
    }
  }
}))

interface QuoteSearchFilterChipsProps {
  show: boolean
  filters: ValueFilter[]
  onDelete: (filter: ValueFilter, valueIndex: number) => void
  invoice?: boolean
}

export default function QuoteSearchFilterChips(props: QuoteSearchFilterChipsProps) {
  const { show, filters, onDelete, invoice } = props
  const classes = useStyles()
  const { options, clientId } = useClientOptions()
  const { isSuperadmin } = useUser()
  const useAcceptedDate = !options?.options?.quotes?.dontCountAcceptedDate
  const { quote: quoteLabel } = getObjectLabels({ options })

  const query = isSuperadmin ? `app_metadata.companyId:"${clientId}" AND app_metadata.active:true` : ''
  const { data, loading: usersLoading } = useSearchUsersQuery({
    variables: {
      query
    },
    fetchPolicy: 'cache-and-network'
  })

  const { canDeactivateSettingsItems } = useFlags()
  const dateFormat = 'MMM D, YYYY'
  const quoteTypeOptions = getQuoteTypeOptions({ options, canDeactivate: !!canDeactivateSettingsItems })
  const quoteTypeFilter = filters.find((filter) => filter.id === 'int_ext') as InFilter
  const activeQuoteTypeOptions = (quoteTypeFilter?.value ?? []).map((value) =>
    quoteTypeOptions.find((option) => option.key === value)
  )

  const statusOptions = getStatusOptions({ isInvoice: invoice })
  const statusFilter = filters.find((filter) => filter.id === 'status.value') as InFilter
  const activeStatusOptions = (statusFilter?.value ?? []).map((value) =>
    statusOptions.find((option) => option.value === value)
  )
  const isViewingAccepted = statusFilter?.value?.includes('accepted')
  const onlyViewingAccepted = isViewingAccepted && statusFilter?.value?.length === 1

  const estimatorFilter = filters.find((filter) => filter.id === 'owner.id') as InFilter
  const estimatorOptions = (data?.searchUsers?.rows || []).map((user) => ({
    label: user.name || (user.user_metadata ? formatName(user.user_metadata) : ''),
    value: removeAuth0Prefix(user.user_id)
  }))
  const activeEstimatorOptions = (estimatorFilter?.value ?? []).map((value) =>
    estimatorOptions.find((option) => option.value === value)
  )

  const dateFilter = filters.find((filter) => filter.id === 'quote_date') as RangeFilter
  const amountFilter = filters.find((filter) => filter.id === 'totals.after_tax') as RangeFilter

  const archivedOptions: DropdownSelectOption[] = [
    { label: 'No Archived', value: ['false'] },
    { label: 'Include Archived', value: ['true', 'false'] },
    { label: 'Only Archived', value: ['true'] }
  ]
  const archivedFilter = filters.find((filter) => filter.id === 'archived')
  const activeArchivedOption = archivedOptions.find(
    (option) =>
      option.value?.length === (archivedFilter?.value as string[])?.length &&
      option.value.every((value) => (archivedFilter?.value as string[])?.includes(value))
  )

  const quoteTagOptions = getQuoteTags({ options })
  const tagsFilter = filters.find((filter) => filter.id === 'tags') as InFilter
  const activeTagsOptions = (tagsFilter?.value ?? []).map((value) =>
    quoteTagOptions.find((option) => option.name === value)
  )

  const getDateFilterChipValue = () => {
    if (!dateFilter?.value) {
      return ''
    }
    if (dateFilter.value[0] && !dateFilter.value[1]) {
      return `After ${moment(dateFilter.value[0]).format(dateFormat)}`
    }
    if (!dateFilter.value[0] && dateFilter.value[1]) {
      return `Before ${moment(dateFilter.value[1]).format(dateFormat)}`
    }
    return `${moment(dateFilter.value[0]).format(dateFormat)} - ${moment(dateFilter.value[1]).format(dateFormat)}`
  }

  const getAmountFilterChipValue = () => {
    if (!amountFilter?.value) {
      return ''
    }
    if (amountFilter.value[0] && !amountFilter.value[1]) {
      return `Over ${formatCurrency({ value: amountFilter.value[0], options })}`
    }
    if (!amountFilter.value[0] && amountFilter.value[1]) {
      return `Under ${formatCurrency({ value: amountFilter.value[1], options })}`
    }
    return `${formatCurrency({ value: amountFilter.value[0], options })} - ${formatCurrency({ value: amountFilter.value[1], options })}`
  }

  return (
    <div className={classes.root}>
      <Collapse show={show}>
        <Paper className={classnames(classes.root, classes.paper)}>
          <Grid container spacing={5}>
            {!!activeQuoteTypeOptions?.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>{invoice ? 'Invoice' : quoteLabel.value} Type</InputLabel>
                <FilterChipGroup
                  classes={{
                    root: classnames(classes.chipGroup, activeQuoteTypeOptions.length === 1 ? classes.longChip : null)
                  }}
                  chips={activeQuoteTypeOptions.map((option, index) => ({
                    label: option.label,
                    onDelete: () => onDelete(quoteTypeFilter, index)
                  }))}
                />
              </Grid>
            )}
            {!!activeStatusOptions?.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>{invoice ? 'Invoice' : quoteLabel.value} Status</InputLabel>
                <FilterChipGroup
                  classes={{
                    root: classnames(classes.chipGroup, activeStatusOptions.length === 1 ? classes.longChip : null)
                  }}
                  chips={activeStatusOptions.map((option, index) => ({
                    label: option.label,
                    onDelete: () => onDelete(statusFilter, index)
                  }))}
                />
              </Grid>
            )}
            {!!activeEstimatorOptions?.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Estimator</InputLabel>
                <FilterChipGroup
                  loading={usersLoading}
                  classes={{
                    root: classnames(classes.chipGroup, activeEstimatorOptions.length === 1 ? classes.longChip : null)
                  }}
                  chips={activeEstimatorOptions.map((option, index) => ({
                    label: option?.label,
                    onDelete: () => onDelete(estimatorFilter, index)
                  }))}
                />
              </Grid>
            )}
            {!!dateFilter?.value && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>
                  {useAcceptedDate
                    ? (() => {
                        if (onlyViewingAccepted) {
                          return 'Accepted'
                        } else if (isViewingAccepted) {
                          return 'Created/Accepted'
                        } else {
                          return 'Created'
                        }
                      })()
                    : 'Created'}{' '}
                  Date
                </InputLabel>
                <FilterChipGroup
                  classes={{ root: classnames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: getDateFilterChipValue(),
                      onDelete: () => onDelete(dateFilter, 0)
                    }
                  ]}
                />
              </Grid>
            )}
            {!!amountFilter?.value && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Amount</InputLabel>
                <FilterChipGroup
                  classes={{ root: classnames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: getAmountFilterChipValue(),
                      onDelete: () => onDelete(amountFilter, 0)
                    }
                  ]}
                />
              </Grid>
            )}
            {!!activeArchivedOption && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Archived</InputLabel>
                <FilterChipGroup
                  classes={{ root: classnames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: activeArchivedOption?.label,
                      onDelete: () => onDelete(archivedFilter, 0)
                    }
                  ]}
                />
              </Grid>
            )}
            {!!activeTagsOptions?.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Tags</InputLabel>
                <FilterChipGroup
                  classes={{
                    root: classnames(classes.chipGroup, activeTagsOptions.length === 1 ? classes.longChip : null)
                  }}
                  chips={activeTagsOptions.map((option, index) => ({
                    label: option.label,
                    color: option.color,
                    onDelete: () => onDelete(tagsFilter, index)
                  }))}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Collapse>
    </div>
  )
}
